import React, { useMemo } from "react";

// Embed mode: partner sites iframe the app pinned to a single restaurant via
// ?embed=1&restaurant=<slug>. The slug is persisted in sessionStorage so the
// mode survives redirects that drop the query string (e.g. Stripe 3DS return).
// ?embed=0 clears it (dev/test escape hatch).

const STORAGE_KEY = "embedRestaurant";

// Read after index.js has moved any old #/ route into the path, so the params
// are normally all in the query; the in-hash read is a fallback.
function readEmbedParams() {
  const preHash = new URLSearchParams(window.location.search);
  const hash = window.location.hash || "";
  const q = hash.indexOf("?");
  const inHash = new URLSearchParams(q >= 0 ? hash.slice(q + 1) : "");
  return {
    embed: preHash.get("embed") ?? inHash.get("embed"),
    restaurant: preHash.get("restaurant") ?? inHash.get("restaurant"),
  };
}

// Inside an iframe? A cross-origin parent throws on access, which also means yes.
function inFrame() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

// The route being opened: "/restaurant/x?menu=y" -> "/restaurant/x".
function currentRoute() {
  return window.location.pathname || "/";
}

function resolveEmbedSlug() {
  const { embed, restaurant } = readEmbedParams();
  try {
    if (embed === "1" && restaurant) {
      sessionStorage.setItem(STORAGE_KEY, restaurant);
      return restaurant;
    }
    if (embed === "0") {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const stored = sessionStorage.getItem(STORAGE_KEY);
    // A remembered pin only holds where embed mode belongs: inside a
    // partner's iframe, or on the pinned restaurant's own order flow (a
    // payment return drops the query). In a normal tab that once opened an
    // embed or short link, going to app.rushmyfood.com must be the full site
    // again, not that one restaurant.
    if (stored && !inFrame() && !isEmbedAllowedPath(currentRoute(), stored)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored;
  } catch (e) {
    return embed === "1" && restaurant ? restaurant : null;
  }
}

// Routes reachable in embed mode besides the pinned restaurant page:
// the checkout/payment/order-tracking flow, the auth flow needed to place an
// order, and the legal pages linked from the login/signup screens.
const EMBED_ALLOWED_PREFIXES = [
  "/checkout",
  "/orders",
  "/order-detail",
  "/paypal",
  "/stripe",
  "/nmi",
  "/login",
  "/registration",
  "/new-login",
  "/login-email",
  "/verify-email",
  "/forgot-password",
  "/verify-forgot-otp",
  "/new-password",
  "/email-sent",
  "/phone-number",
  "/verify-phone",
  "/privacy",
  "/terms",
];

export function isEmbedAllowedPath(pathname, embedSlug) {
  if (pathname.startsWith("/restaurant/")) {
    let seg = pathname.slice("/restaurant/".length).replace(/\/+$/, "");
    try {
      seg = decodeURIComponent(seg);
    } catch (e) {
      // malformed escape sequence — compare raw
    }
    return seg === embedSlug;
  }
  return EMBED_ALLOWED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  );
}

const EmbedContext = React.createContext({ isEmbed: false, embedSlug: null });

export function EmbedProvider({ children }) {
  const embedSlug = useMemo(resolveEmbedSlug, []);
  const value = useMemo(
    () => ({ isEmbed: !!embedSlug, embedSlug }),
    [embedSlug]
  );
  return (
    <EmbedContext.Provider value={value}>{children}</EmbedContext.Provider>
  );
}

export default EmbedContext;
